import { UUID } from 'magpie-types';
import type { Opaque, Prettify } from '../types';
import { getUUID } from '../utils';

const MessageKind: unique symbol = Symbol();
type MessageKind = typeof MessageKind;

const MsgTypeKind: unique symbol = Symbol();
type MsgTypeKind = typeof MsgTypeKind;

export type MessageType<T extends string> = Opaque<
  MsgTypeKind,
  `@@magpie-lib/${T}`
>;

export const messageType = <T extends string>(type: T) =>
  `@@magpie-lib/${type}` as MessageType<T>;

type IsNever<X, T, F> = [X] extends [never] ? T : F;

export type Message<Type extends string, Payload = never> = Opaque<
  MessageKind,
  Prettify<
    {
      id: UUID;
      type: MessageType<Type>;
    } & ([Payload] extends [never] ? {} : { payload: Payload })
  >
>;

export type AnyMessage = Message<string, any> | Message<string, never>;

export type MessageBuilder<
  Type extends string,
  Arg extends unknown,
  Payload extends unknown,
> = IsNever<
  Arg,
  () => Message<Type, Payload>,
  (arg: Arg) => Message<Type, Payload>
> & {
  type: Type;
  match(message: any): message is Message<Type, Payload>;
};

type PayloadBuilder<Payload, Arg = never> = IsNever<
  Arg,
  () => Payload,
  (arg: Arg) => Payload
>;

export type InferMessageType<MB> = MB extends (...args: any[]) => infer M
  ? M
  : never;

export function defineMessage<Type extends string>(
  type: Type
): MessageBuilder<Type, never, never>;
export function defineMessage<Type extends string, Payload, Arg = never>(
  type: Type,
  payloadBuilder: PayloadBuilder<Payload, Arg>
): MessageBuilder<Type, Arg, Payload>;
export function defineMessage(type: string, payloadBuilder?: Function): any {
  type Type = typeof type;
  type _PayloadBuilder = typeof payloadBuilder;
  type Payload = _PayloadBuilder extends (...args: any[]) => any
    ? ReturnType<_PayloadBuilder>
    : never;

  const alias = messageType(type);

  const extra = {
    type: alias,
    match: (message: AnyMessage): message is Message<Type, Payload> => {
      return !!message && typeof message === 'object' && message.type === alias;
    },
  };
  const messageBuilder = payloadBuilder
    ? (arg: any) => ({
        id: getUUID(),
        type: alias,
        payload: payloadBuilder(arg),
      })
    : () => ({ type: alias, id: getUUID() });
  return Object.assign(messageBuilder, extra);
}
